import { IconLayer } from '@deck.gl/layers';
import uavIconUrl from '../../assets/icons/demo/demo-auv-trimaran.png';
import uavThreatIconUrl from '../../assets/icons/vessels/icon-threat.png';
import auvRasterUrl from '../../assets/icons/demo/autonomous-underwater-vehicle.png';
import { isDemoThreatMissileVessel } from '../../utils/demoThreatMissileVessel';
import { isDemoAuvRasterVessel } from '../../utils/demoAuvRasterVessel';
import {
  resolveVesselMaritimeHeadingDeg,
  deckIconAngleFromMaritimeHeadingDeg,
  VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO
} from '../../utils/vesselMapIconAngle';

// Mask: false — full-color PNGs; dimensions match demo assets in src/assets/icons/demo/
const ICON_MAPPING_FRIENDLY = {
    icon: { x: 0, y: 0, width: 1024, height: 558, mask: false, anchorY: 279 }
};
const ICON_MAPPING_THREAT = {
    icon: { x: 0, y: 0, width: 1280, height: 698, mask: false, anchorY: 349 }
};
const ICON_MAPPING_AUV_RASTER = {
    icon: { x: 0, y: 0, width: 1024, height: 252, mask: false, anchorY: 126 }
};

/**
 * Generates Deck.gl IconLayer(s) for the animated UAV vessel(s).
 * Trimaran default; icon-threat for demo threat USV; autonomous-underwater-vehicle.png for designated AUV rows (demoAuvRasterVessel.js).
 * Returns an array of layers to allow the caller to spread them in.
 * @param {Object|Array} vesselStates - Interpolated state(s)
 * @returns {IconLayer[]|null}
 */
export const generateVesselLayer = (vesselStates, options = {}) => {
    if (!vesselStates || (Array.isArray(vesselStates) && vesselStates.length === 0)) return null;

    const { defaultSizePixels = 24, threatSizePixels = 32 } = options;

    const states = Array.isArray(vesselStates) ? vesselStates : [vesselStates];

    return states.map((vesselState, idx) => {
        const useAuvRaster = isDemoAuvRasterVessel(vesselState);
        const useMissile = !useAuvRaster && isDemoThreatMissileVessel(vesselState);
        const iconMode = useAuvRaster ? 'auv' : useMissile ? 'threat' : 'friendly';
        const atlas = useAuvRaster ? auvRasterUrl : useMissile ? uavThreatIconUrl : uavIconUrl;
        const mapping = useAuvRaster
            ? ICON_MAPPING_AUV_RASTER
            : useMissile
              ? ICON_MAPPING_THREAT
              : ICON_MAPPING_FRIENDLY;
        const pixelSize = useMissile ? threatSizePixels : defaultSizePixels;
        const trackId = vesselState.trackId !== undefined ? vesselState.trackId : idx;
        const bow =
          iconMode === 'auv'
            ? VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO.auv
            : useMissile
              ? VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO.threat
              : VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO.friendly;
        const hdg = resolveVesselMaritimeHeadingDeg(vesselState);
        const iconAngle = hdg == null ? 0 : deckIconAngleFromMaritimeHeadingDeg(hdg, bow);

        return new IconLayer({
            id: `vessel-layer-${trackId}`,
            data: [vesselState],
            pickable: true,
            iconAtlas: atlas,
            iconMapping: mapping,
            getIcon: () => 'icon',
            sizeUnits: 'pixels',
            sizeScale: 1,
            getPosition: d => [d.lon, d.lat],
            getSize: pixelSize,
            getColor: [255, 255, 255, 255],
            getAngle: () => iconAngle,
            billboard: false,

            updateTriggers: {
                getPosition: [vesselState.lat, vesselState.lon],
                getAngle: [
                    iconAngle,
                    vesselState.movementBearing,
                    vesselState.heading_deg,
                    vesselState.direction
                ],
                iconAtlas: [iconMode],
                iconMapping: [iconMode]
            }
        });
    });
};